import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Icon,
  Paper,
  Card,
  Tooltip,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';

function CostbooksTab({ product, renderFieldsProp }) {
  const [expandedCostbook, setExpandedCostbook] = useState({});

  const groupedCosts = useMemo(() => {
    const costs = product?.related_data?.pricingModel?.costs || [];
    if (costs.length === 0) {
      return {};
    }
    return costs.reduce((acc, cost) => {
      // Use a more specific name if available, otherwise default.
      const groupName = cost.name || `Standard Costs`;
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(cost);
      return acc;
    }, {});
  }, [product]);

  const costbookNames = Object.keys(groupedCosts);

  if (costbookNames.length === 0) {
    return (
      <ArgonBox p={3}>
        <ArgonTypography variant="body2">No Costs available for this product.</ArgonTypography>
      </ArgonBox>
    );
  }

  const handleCostbookClick = (costbookName) => {
    setExpandedCostbook((prevState) => ({
      ...prevState,
      [costbookName]: !prevState[costbookName],
    }));
  };

  return (
    <ArgonBox p={3}>
      {costbookNames.map((costbookName) => {
        const costsInGroup = groupedCosts[costbookName];
        const isExpanded = expandedCostbook[costbookName];

        // --- FIX #1: Get the fields from the FIRST cost record in the group ---
        // We'll display these as the "Costbook" level fields.
        const representativeFields = costsInGroup[0]?.fields || {};

        return (
          <Card key={costbookName} sx={{ marginBottom: '1rem', overflow: 'hidden' }}>
            <ArgonBox>
              <ArgonBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => handleCostbookClick(costbookName)}
                style={{ cursor: 'pointer' }}
                p={1.5}
              >
                <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                  {costbookName}
                </ArgonTypography>
                <IconButton
                  aria-label="expand costbook"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCostbookClick(costbookName);
                  }}
                >
                  <Icon>{isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                </IconButton>
              </ArgonBox>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <ArgonBox p={2} pt={1}>
                  {/* --- RENDER THE REPRESENTATIVE FIELDS --- */}
                  {renderFieldsProp(representativeFields)}

                  <ArgonBox mt={3}>
                    <TableContainer component={Paper} elevation={0} variant="outlined">
                      <Table size="small" aria-label="cost entries">
                        <TableHead sx={{ display: 'contents' }}>
                          <TableRow>
                            <TableCell>
                              <ArgonTypography variant="caption" fontWeight="bold">
                                Unit Cost
                              </ArgonTypography>
                            </TableCell>
                            <TableCell>
                              <ArgonTypography variant="caption" fontWeight="bold">
                                Status
                              </ArgonTypography>
                            </TableCell>
                            {/* --- FIX #2: REMOVED THE ID COLUMN --- */}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {costsInGroup.map((cost) => (
                            <TableRow key={cost.sfId || cost.id}>
                              <TableCell>{cost.fields?.SBQQ__UnitCost__c ?? 'N/A'}</TableCell>
                              <TableCell>
                                <Tooltip title={cost.fields?.SBQQ__Active__c === 'true' ? 'Active' : 'Inactive'}>
                                  {cost.fields?.SBQQ__Active__c === 'true' ? (
                                    <CircleIcon color="success" sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                                  ) : (
                                    <CircleIcon color="disabled" sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                                  )}
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </ArgonBox>
                </ArgonBox>
              </Collapse>
            </ArgonBox>
          </Card>
        );
      })}
    </ArgonBox>
  );
}

export default CostbooksTab;
